import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBankAccountCashFlowOverview,
  getBankAccountSummaryByBusinessEntity,
  getBankAccountSummaryByCategory,
  updateBankAccountOrder,
} from '~apis/cash-flow-overview/bank-account';
import { FundRequest, getFunds } from '~apis/cash-flow-overview/funds';
import { SecuritiesRequest, getSecurities } from '~apis/cash-flow-overview/securities';
import { BankAccountsQueryKey } from '~hooks/queries/bank-accounts/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { BankAccountCashFlowRequest } from '~models/cashFlow';

interface CompanyIdentifier {
  companyId: string;
}

export const BankAccountCashFlowsQueryKey = {
  default: (identifier: CompanyIdentifier) => [
    ...BankAccountsQueryKey.list(identifier),
    'cashFlows',
  ],
  list: ({ request, companyId }: CompanyIdentifier & { request?: BankAccountCashFlowRequest }) =>
    [...BankAccountCashFlowsQueryKey.default({ companyId }), 'list', request].filter(Boolean),
  funds: ({ request, companyId }: CompanyIdentifier & { request?: FundRequest }) =>
    [...BankAccountCashFlowsQueryKey.default({ companyId }), 'funds', request].filter(Boolean),
  securities: ({ request, companyId }: CompanyIdentifier & { request?: SecuritiesRequest }) =>
    [...BankAccountCashFlowsQueryKey.default({ companyId }), 'securities', request].filter(Boolean),
  summary: ({ companyId, request }: CompanyIdentifier & { request?: BankAccountCashFlowRequest }) =>
    [...BankAccountCashFlowsQueryKey.list({ companyId }), 'summary', request].filter(Boolean),
  summaryByCategory: (identifier: CompanyIdentifier & { request?: BankAccountCashFlowRequest }) => [
    ...BankAccountCashFlowsQueryKey.summary(identifier),
    'byCategory',
  ],
  summaryByBusinessEntity: (
    identifier: CompanyIdentifier & { request?: BankAccountCashFlowRequest },
  ) => [...BankAccountCashFlowsQueryKey.summary(identifier), 'byBusinessEntity'],
};

export function useBankAccountCashFlows(request: BankAccountCashFlowRequest) {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: BankAccountCashFlowsQueryKey.list({ companyId, request }),
    queryFn: () => getBankAccountCashFlowOverview({ companyId, request }),
    enabled: !!companyId,
    refetchOnMount: true,
  });
}

export function useFundsAccountCashFlows(request: FundRequest) {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: BankAccountCashFlowsQueryKey.funds({ companyId, request }),
    queryFn: () => getFunds({ ...request }),
    enabled: !!companyId,
  });
}

export function useSecuritiesAccountCashFlows(request: SecuritiesRequest) {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: BankAccountCashFlowsQueryKey.securities({ companyId, request }),
    queryFn: () => getSecurities({ ...request }),
    enabled: !!companyId,
  });
}

export function useBankAccountSummaryByCategory(request: BankAccountCashFlowRequest) {
  const { companyId } = useDemoAwareCompanyId();

  return useQuery({
    queryKey: BankAccountCashFlowsQueryKey.summaryByCategory({ companyId, request }),
    queryFn: () => getBankAccountSummaryByCategory({ companyId, request }),
    enabled: !!companyId,
  });
}

export function useBankAccountSummaryByBusinessEntity(request: BankAccountCashFlowRequest) {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: BankAccountCashFlowsQueryKey.summaryByBusinessEntity({ companyId, request }),
    queryFn: () => getBankAccountSummaryByBusinessEntity({ companyId, request }),
    enabled: !!companyId,
  });
}

export function useUpdateBankAccountOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBankAccountOrder,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(
        BankAccountCashFlowsQueryKey.list({ companyId: variables.companyId }),
      );
    },
  });
}
